import { useState, useEffect, useMemo, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Button, Dropdown, Switch } from "antd";
import { DownOutlined, UserOutlined } from "@ant-design/icons";
import { BiWorld } from "react-icons/bi";
import { useTranslation } from "react-i18next";
import { preloadNamespacesForRoute, preloadRemainingNamespaces } from "i18n";
import { getDefaultAuthenticatedPath } from "modules/appModuleAccess";
import { isColorDark } from "utils/styleUtils";
import useAuthStore from "stores/useAuthStore";
import { useResolvedPostLoginBranding } from "hooks/useBranding";
import { DEMO_MODE_ACTIVE, getUseFallbackFiles, setUseFallbackFiles, resetUseFallbackFiles } from "api/utils/servicesUtils";
import GlobalSearchBarDesign from "./GlobalSearchBarDesign";
import UserMenu from "./UserMenu";
import DynamicTooltip from "components/universal/DynamicTooltip/DynamicTooltip";
import "./Header.css";

const DEFAULT_LANGUAGES = "en,es";
const LANGUAGE_LABELS = {
  en: "English",
  en_proman: "English",
  es: "Español",
  es_emcasa: "EMCASA",
  es_csg: "CSG",
};
const LANGUAGE_MENU_ITEMS = [
  { key: "en", label: LANGUAGE_LABELS.en },
  { key: "en_proman", label: LANGUAGE_LABELS.en_proman },
  { key: "es", label: LANGUAGE_LABELS.es },
  { key: "es_emcasa", label: LANGUAGE_LABELS.es_emcasa },
  { key: "es_csg", label: LANGUAGE_LABELS.es_csg },
];

function normalizeAvailableLanguages(rawValue) {
  const fallback = DEFAULT_LANGUAGES.split(",");

  const cleanEntry = (v) => String(v).trim().replace(/^["']|["']$/g, "");

  const fromArray = (arr) =>
    arr
      .flatMap((v) => String(v).split(/[,\n\t]+/))
      .map(cleanEntry)
      .filter(Boolean);

  // Already an array (defensive, in case source ever changes from process.env)
  if (Array.isArray(rawValue)) {
    const entries = fromArray(rawValue);
    return entries.length > 0 ? entries : fallback;
  }

  let value = rawValue;

  // Not set, not a string, or empty after trim -> use default
  if (typeof value !== "string" || value.trim() === "") {
    value = DEFAULT_LANGUAGES;
  }

  value = value.trim();

  // Strip one layer of wrapping quotes: "en,es" or 'en,es' -> en,es
  if (
    (value.startsWith('"') && value.endsWith('"')) ||
    (value.startsWith("'") && value.endsWith("'"))
  ) {
    value = value.slice(1, -1).trim();
  }

  // Handle accidental JSON array string: '["en","es"]'
  if (value.startsWith("[") && value.endsWith("]")) {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        const entries = fromArray(parsed);
        return entries.length > 0 ? entries : fallback;
      }
    } catch {
      // not valid JSON, fall through to comma-split handling below
    }
  }

  const entries = value
    .split(/[,\n\t]+/)
    .map(cleanEntry)
    .filter(Boolean);

  return entries.length > 0 ? entries : fallback;
};

function resolveConfiguredLanguage(storedLanguage, availableLanguages) {
  const preferredLanguage = process.env.REACT_APP_PREFERRED_LANGUAGE || "en";
  const fallbackLanguage = availableLanguages.includes(preferredLanguage)
    ? preferredLanguage
    : availableLanguages[0] || "en";

  return availableLanguages.includes(storedLanguage) ? storedLanguage : fallbackLanguage;
};

const availableLanguages = normalizeAvailableLanguages(
  process.env.REACT_APP_AVAILABLE_LANGUAGES
);
const languageMenuItems = LANGUAGE_MENU_ITEMS.filter((item) =>
  availableLanguages.includes(item.key)
);

/**
 * @param {Function} onLogout Function to logout user
 * @param {Boolean} hideHeaderTools If true hides the global search
 */
const Header = ({ onLogout, hideHeaderTools }) => {
  const { t, i18n } = useTranslation("common");
  const location = useLocation();
  const userData = useAuthStore((s) => s.userData);
  const { headerBranding, headerBackgroundColor } = useResolvedPostLoginBranding();
  const isHeaderBackgroundDark = isColorDark(headerBackgroundColor);
  const hasHeaderLogo = Boolean(headerBranding.logoUrl);
  const hasHeaderText = Boolean(headerBranding.text);
  const hasCombinedHeaderBranding = hasHeaderLogo && hasHeaderText;

  const [language, setLanguage] = useState(i18n.language || "en");
  const [useFallbackFiles, setUseFallbackFilesState] = useState(getUseFallbackFiles);

  // When demo mode is off, ensure the toggle is cleared from localStorage
  useEffect(() => {
    if (!DEMO_MODE_ACTIVE) {
      resetUseFallbackFiles();
      setUseFallbackFilesState(false);
    }
  }, [DEMO_MODE_ACTIVE]);

  const handleFallbackFilesToggle = useCallback((checked) => {
    setUseFallbackFiles(checked);
    setUseFallbackFilesState(checked);
  }, []);

  const changeLanguageForCurrentRoute = useCallback(
    async (nextLanguage) => {
      setLanguage(nextLanguage);
      localStorage.setItem("i18nextLng", nextLanguage);

      try {
        await preloadNamespacesForRoute(location.pathname, nextLanguage);
        await i18n.changeLanguage(nextLanguage);
        void preloadRemainingNamespaces(nextLanguage);
      } catch (error) {
        console.error("Language change failed:", error);
        await i18n.changeLanguage(nextLanguage);
      }
    },
    [i18n, location.pathname]
  );

  const syncLanguage = useCallback(() => {
    const langChoice = localStorage.getItem("i18nextLng");
    const nextLanguage = resolveConfiguredLanguage(langChoice, availableLanguages);
    setLanguage(nextLanguage);
    localStorage.setItem("i18nextLng", nextLanguage);
    if (i18n.language !== nextLanguage) {
      void changeLanguageForCurrentRoute(nextLanguage);
    }
  }, [i18n.language, availableLanguages, changeLanguageForCurrentRoute]);

  useEffect(() => {
    syncLanguage();
  }, [syncLanguage]);

  const handleMenuClick = useCallback((e) => {
    // Ignore clicks from the fallback toggle item
    if (e.key === "fallback_files_toggle") return;
    void changeLanguageForCurrentRoute(e.key);
  }, [changeLanguageForCurrentRoute]);

  const languageItems = useMemo(() => {
    const items = [...languageMenuItems];

    if (DEMO_MODE_ACTIVE) {
      items.push(
        { type: "divider", key: "div-fallback" },
        {
          key: "fallback_files_toggle",
          className: "language-menu-fallback-toggle",
          label: (
            <div
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "5px" }}
              onClick={(e) => e.stopPropagation()}
            >
              <span>{t("header.fallback_files", "Use fallback files")}</span>

              <Switch
                size="small"
                checked={useFallbackFiles}
                onChange={handleFallbackFilesToggle}
              />
            </div>
          ),
        }
      );
    }

    return items;
  }, [t, useFallbackFiles, handleFallbackFilesToggle]);

  return (
    <div
      className="app-header-container"
      data-theme={isHeaderBackgroundDark ? "dark" : "light"}
      style={{
        background: headerBackgroundColor
      }}
    >
      <div className="app-header-container-inner">
        <div className="app-header-brand-col">
          <NavLink
            className={`app-header-home-link${hasCombinedHeaderBranding ? " app-header-home-link--combined" : ""}`}
            to={getDefaultAuthenticatedPath(userData)}
            aria-label={hasHeaderText ? `Go to ${headerBranding.text}` : "Go to home"}
          >
            {hasHeaderLogo && (
              <img
                src={headerBranding.logoUrl}
                alt={hasHeaderText ? `${headerBranding.text} logo` : "Brand logo"}
                className="app-header-brand-logo"
              />
            )}

            {hasHeaderText && (
              <DynamicTooltip
                title={headerBranding.text}
                className="app-header-brand-name-wrapper"
              >
                <h2 className="app-header-brand-name">{headerBranding.text}</h2>
              </DynamicTooltip>
            )}
          </NavLink>
        </div>

        <div className="app-header-search-col">
          <div className="app-header-search-wrapper">
            {!hideHeaderTools && (
              <GlobalSearchBarDesign />
            )}
          </div>
        </div>

        <div className="app-header-right-col">
          <div className="app-header-right">
            <Dropdown
              menu={{ items: languageItems, onClick: handleMenuClick }}
              trigger={["click"]}
            >
              <Button type="text" className="app-header-dropdown-button">
                <BiWorld size={20} className="app-header-language-icon" />
                {LANGUAGE_LABELS[language] ?? language}
                <DownOutlined className="app-header-dropdown-icon" />
              </Button>
            </Dropdown>

            <UserMenu
              onLogout={onLogout}
              customTriggerRender={
                <Button type="text" className="app-header-dropdown-button">
                  <UserOutlined style={{ fontSize: 20 }} />
                </Button>
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
